import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns' 

interface RiderDeductionsTabProps {
  deductions: any[];
  dateFrom?: Date | null;
  dateTo?: Date | null;
  onDateFromChange?: (d: Date | null) => void;
  onDateToChange?: (d: Date | null) => void;
  onApplyFilters?: () => void;
  onClearFilters?: () => void;
}

const RiderDeductionsTab: React.FC<RiderDeductionsTabProps> = ({ deductions, dateFrom, dateTo, onDateFromChange, onDateToChange, onApplyFilters, onClearFilters }) => {
  const [selectedRow, setSelectedRow] = useState<any | null>(null);
  const fmt = (v: any) => (v != null ? new Intl.NumberFormat('en-IN').format(v) : '-');

  const closeDialog = () => setSelectedRow(null);

  return (
    <Box>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            MIS Deduction Rows
          </Typography>
                {/* Date filters for deductions */}
                <Box sx={{ mt: 2, mb: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={3}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          label="From"
                          value={dateFrom}
                          onChange={(d) => onDateFromChange?.(d)}
                          slotProps={{ textField: { fullWidth: true } }}
                        />
                      </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          label="To"
                          value={dateTo}
                          onChange={(d) => onDateToChange?.(d)}
                          slotProps={{ textField: { fullWidth: true } }}
                        />
                      </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} md={6} sx={{ textAlign: 'right' }}>
                      <Button variant="contained" onClick={() => onApplyFilters?.()} sx={{ mr: 1 }}>
                        Apply
                      </Button>
                      <Button variant="outlined" onClick={() => onClearFilters?.()}>
                        Clear
                      </Button>
                    </Grid>
                  </Grid>
                </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Period</TableCell>
                  <TableCell>Month / Year</TableCell>
                  <TableCell align="right">TDS</TableCell>
                  <TableCell align="right">Client Deduction</TableCell>
                  <TableCell align="right">SD</TableCell>
                  <TableCell align="right">Damage Cost</TableCell>
                  <TableCell align="right">Damage Cost Deducted</TableCell>
                  <TableCell align="right">Fleet</TableCell>
                  <TableCell align="right">Traffic Fine</TableCell>
                  <TableCell align="right">Traffic Fine Deducted</TableCell>
                  <TableCell align="right">Battery Swap</TableCell>
                  <TableCell align="right">On Hold</TableCell>
                  <TableCell align="right">Others</TableCell>
                  <TableCell align="right">COD</TableCell>
                  <TableCell align="right">EV Rent</TableCell>
                  <TableCell align="right">EV Rent Deducted</TableCell>
                  <TableCell align="right">Insurance</TableCell>
                  <TableCell align="right">Total Deduction</TableCell>
                  <TableCell align="right">Final Deducted</TableCell>
                  <TableCell align="right">Pending</TableCell>
                  <TableCell align="center">Details</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deductions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={21} sx={{ textAlign: 'center', py: 4 }}>
                      No MIS deduction rows found
                    </TableCell>
                  </TableRow>
                ) : (
                  deductions.map((row: any) => (
                    <TableRow
                      key={row.id}
                      hover
                      sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover' } }}
                      onClick={() => setSelectedRow(row)}
                    >
                      <TableCell>
                        {row.periodStartDate ? new Date(row.periodStartDate).toLocaleDateString('en-IN') : '-'} - {row.periodEndDate ? new Date(row.periodEndDate).toLocaleDateString('en-IN') : '-'}
                      </TableCell>
                      <TableCell>{row.month || '-'} / {row.year || '-'}</TableCell>
                      <TableCell align="right">{fmt(row.tds)}</TableCell>
                      <TableCell align="right">{fmt(row.clientDeduction)}</TableCell>
                      <TableCell align="right">{fmt(row.sd)}</TableCell>
                      <TableCell align="right">{fmt(row.damageCost)}</TableCell>
                      <TableCell align="right">{fmt(row.damageCostDeducted)}</TableCell>
                      <TableCell align="right">{fmt(row.fleet)}</TableCell>
                      <TableCell align="right">{fmt(row.trafficFine)}</TableCell>
                      <TableCell align="right">{fmt(row.trafficFineDeducted)}</TableCell>
                      <TableCell align="right">{fmt(row.batterySwap)}</TableCell>
                      <TableCell align="right">{fmt(row.onHold)}</TableCell>
                      <TableCell align="right">{fmt(row.others)}</TableCell>
                      <TableCell align="right">{fmt(row.cod)}</TableCell>
                      <TableCell align="right">{fmt(row.evRent)}</TableCell>
                      <TableCell align="right">{fmt(row.evRentDeducted)}</TableCell>
                      <TableCell align="right">{fmt(row.Insurance)}</TableCell>
                      <TableCell align="right">{fmt(row.Deduction)}</TableCell>
                      <TableCell align="right">{fmt(row.FinalDeducted)}</TableCell>
                      <TableCell align="right">{fmt(row.pending)}</TableCell>
                      <TableCell align="center">
                        <Button size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); setSelectedRow(row); }}>
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Dialog open={Boolean(selectedRow)} onClose={closeDialog} maxWidth="sm" fullWidth>
            <DialogTitle>Deduction Pending Details</DialogTitle>
            <DialogContent>
              {selectedRow ? (
                <Box sx={{ pt: 1 }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Period
                      </Typography>
                      <Typography>
                        {selectedRow.periodStartDate ? new Date(selectedRow.periodStartDate).toLocaleDateString('en-IN') : '-'} - {selectedRow.periodEndDate ? new Date(selectedRow.periodEndDate).toLocaleDateString('en-IN') : '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Month / Year
                      </Typography>
                      <Typography>{selectedRow.month || '-'} / {selectedRow.year || '-'}</Typography>
                    </Grid>
                  </Grid>

                  <Divider sx={{ my: 2 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Damage Pending
                      </Typography>
                      <Typography>{fmt(selectedRow.damageCost - (selectedRow.damageCostDeducted ?? 0))}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Traffic Pending
                      </Typography>
                      <Typography>{fmt(selectedRow.trafficFine - (selectedRow.trafficFineDeducted ?? 0))}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Ev Rent Pending
                      </Typography>
                      <Typography>{fmt(selectedRow.evRent - (selectedRow.evRentDeducted ?? 0))}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Pending Total
                      </Typography>
                      <Typography>{fmt(selectedRow.pending)}</Typography>
                    </Grid>
                  </Grid>
                </Box>
              ) : null}
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDialog}>Close</Button>
            </DialogActions>
          </Dialog>
        </CardContent>
      </Card>
    </Box>
  );
};

export default RiderDeductionsTab;
